import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

interface UseScrollToSectionOptions {
  sectionIds: string[];
  offsetPx?: number;
  onlyOnLoad?: boolean;
  scrollContainerSelector?: string;
}

/**
 * Custom hook that scrolls to a section based on the hash in the URL.
 * Handles HashRouter format and includes retry logic for DOM readiness.
 * @param sectionIds - Array of section IDs to scroll to
 * @param offsetPx - Offset in pixels from the top of the scroll container to the top of the section
 * @param onlyOnLoad - If true (default), only scrolls once on initial page load. If false, scrolls every time the hash changes.
 * @param scrollContainerSelector - CSS selector for the scroll container (default: "#main-page-wrapper")
 */
export function useScrollToSection({
  sectionIds,
  offsetPx = 0,
  onlyOnLoad = true,
  scrollContainerSelector = "#main-page-wrapper",
}: UseScrollToSectionOptions) {
  const location = useLocation();
  const hash = location.hash || window.location.hash;
  const hasScrolledToRef = useRef(false);

  useEffect(() => {
    // If onlyOnLoad is true and we've already scrolled, don't do anything
    if (onlyOnLoad && hasScrolledToRef.current) return;
    if (!hash) return;

    // Split by # and find the section ID (the last part that matches a section)
    const hashParts = hash.split("#").filter(Boolean); // filter out empty strings
    let sectionId: string | undefined;

    // Check each hash part to see if it's a valid section ID
    for (let i = hashParts.length - 1; i >= 0; i--) {
      const part = hashParts[i];
      // Remove leading slashes and check if it matches a section ID
      const cleanPart = part.replace(/^\/+/, "");
      if (sectionIds.includes(cleanPart)) {
        sectionId = cleanPart;
        break;
      }
    }

    // Scroll to the section if found
    if (!sectionId) return;

    const scrollContainer = document.querySelector(scrollContainerSelector);
    if (!scrollContainer) return;

    // Function to attempt scrolling with retry logic
    const scrollToSection = (retries = 5) => {
      if (!sectionId) return;
      const element = document.getElementById(sectionId);
      if (element && scrollContainer) {
        // Calculate position relative to scroll container
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        // Calculate scroll position
        const scrollTop = (scrollContainer as HTMLElement).scrollTop;
        const elementTop = elementRect.top - containerRect.top + scrollTop;
        const targetScroll = elementTop - offsetPx;

        (scrollContainer as HTMLElement).scrollTo({
          top: targetScroll,
          behavior: "smooth",
        });

        // Mark as successfully scrolled to prevent future scrolls (only if onlyOnLoad is true)
        if (onlyOnLoad) {
          hasScrolledToRef.current = true;
        }
      } else if (retries > 0) {
        // Retry if element not found yet
        setTimeout(() => scrollToSection(retries - 1), 100);
      }
    };

    // Initial delay to ensure DOM is fully rendered, then attempt scroll
    const timeoutId = setTimeout(() => scrollToSection(), 200);
    return () => clearTimeout(timeoutId);
  }, [hash, sectionIds, offsetPx, onlyOnLoad, scrollContainerSelector]);
}
